
import { GoogleGenAI } from "@google/genai";
import type { MarketIntelResponse } from '../types';

/**
 * Generates a product description using Gemini.
 * Adheres to the '@google/genai' coding guidelines.
 */
export async function generateDescription(productName: string): Promise<string> {
  const getMockResponse = async () => {
    await new Promise(resolve => setTimeout(resolve, 800));
    return `Fresh ${productName} sourced locally. High quality and grown with care, straight from our farm to your table.`;
  };

  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    const prompt = `Generate a compelling, brief, and data-efficient description for an agricultural product named "${productName}". Focus on freshness and quality. Keep it under 40 words.`;

    const response = await ai.models.generateContent({
      model: 'gemini-3-flash-preview',
      contents: prompt,
    });
    
    return response.text || await getMockResponse();
  } catch (error) {
    console.warn("Error generating description with Gemini API:", error);
    return getMockResponse();
  }
}

/**
 * Uses Gemini with Google Search grounding to provide real-time market data.
 * Phase 11: Production Readiness - External Intel Integration.
 */
export async function searchMarketIntel(query: string): Promise<MarketIntelResponse> {
  try {
    const ai = new GoogleGenAI({ apiKey: process.env.API_KEY });
    
    const response = await ai.models.generateContent({
      model: "gemini-3-flash-preview",
      contents: `Provide a concise answer about current agricultural market trends, prices, or seasons for this query in Suriname: "${query}". Keep it helpful for farmers or buyers.`,
      config: {
        tools: [{ googleSearch: {} }],
      },
    });

    const answer = response.text || "I couldn't find specific market data for that right now.";
    const chunks = response.candidates?.[0]?.groundingMetadata?.groundingChunks || [];
    
    // Extract sources from grounding metadata
    const sources = chunks
      .filter(chunk => chunk.web)
      .map(chunk => ({
        title: chunk.web?.title || "Market Source",
        uri: chunk.web?.uri || "#"
      }));

    return { answer, sources };
  } catch (error) {
    console.error("Market Intel Error:", error);
    return {
      answer: "Market intel services are currently offline. Please try again later.",
      sources: []
    };
  }
}
